import type {
  ButtonBorderRadius,
  ButtonShadow,
  ButtonBorderWidth,
  ButtonFontWeight,
  ButtonTextTransform,
} from '@/lib/constants/theme'
import type { ContactMethod } from '@/lib/constants/contact-method'
import type { VoucherSource, VoucherStatus } from './voucher'

export const FallbackCoverId = 'mrunjkqldk6ojyfxjvj1'

export const getFallbackPrivacyPolicyDocumentUrl = (locale: string) => {
  switch (locale) {
    case 'it':
      return 'https://aloesuite.s3.eu-west-3.amazonaws.com/privacy-policy-documents/Privacy+Policy.pdf'
    default:
      return 'https://aloesuite.s3.eu-west-3.amazonaws.com/privacy-policy-documents/Privacy+Policy_en.pdf'
  }
}

export enum CategorySlug {
  Tours = 'tours',
  Transfer = 'transfer',
  Wellness = 'wellness',
  Rental = 'rental',
  Ticketing = 'ticketing',
  Gastronomy = 'gastronomy',
  Restaurant = 'restaurant',
  Services = 'services',
  Animation = 'animation',
  Sport = 'sport',
  Facilities = 'facilities',
}

export enum CategorySetting {
  UseBookingEmail = 'use_booking_email',
  MarkExpiredAsDelay = 'mark_expired_as_delay',
}

export enum Role {
  Admin = 'admin',
  Seller = 'seller',
  Organizer = 'organizer',

  Staff = 'staff',
  Manager = 'manager',
  Superadmin = 'superadmin',
  Coordinator = 'coordinator',
}

export enum Permission {
  Reports = 'reports',

  CompaniesEdit = 'companies.edit',
  CompaniesCreate = 'companies.create',
  CompaniesDelete = 'companies.delete',
  CompaniesView = 'companies.view',
  CompaniesViewAny = 'companies.viewAny',
  CompaniesOptions = 'companies.options',

  UsersEdit = 'users.edit',
  UsersCreate = 'users.create',
  UsersDelete = 'users.delete',
  UsersView = 'users.view',
  UsersViewAny = 'users.viewAny',

  ExperiencesEdit = 'experiences.edit',
  ExperiencesCreate = 'experiences.create',
  ExperiencesDelete = 'experiences.delete',
  ExperiencesView = 'experiences.view',
  ExperiencesViewAny = 'experiences.viewAny',
  ExperiencesAssignStaff = 'experiences.assignStaff',

  ExternalExperiencesViewAny = 'externalExperiences.viewAny',
  ExternalExperiencesPick = 'externalExperiences.pick',

  GroupExperiencesViewAny = 'groupExperiences.viewAny',
  GroupExperiencesPick = 'groupExperiences.pick',

  BookingsViewAny = 'bookings.viewAny',
  BookingsView = 'bookings.view',
  BookingsEdit = 'bookings.edit',
  BookingsCancel = 'bookings.cancel',
  BookNowInternal = 'book_now.internal',
  BookNowExternal = 'book_now.external',

  ProductsCreate = 'products.create',
  ProductsEdit = 'products.edit',
  ProductsDelete = 'products.delete',
  ProductsView = 'products.view',
  ProductsViewAny = 'products.viewAny',

  StoresCreate = 'stores.create',
  StoresEdit = 'stores.edit',
  StoresDelete = 'stores.delete',
  StoresView = 'stores.view',
  StoresViewAny = 'stores.viewAny',

  WidgetsCreate = 'widgets.create',
  WidgetsEdit = 'widgets.edit',
  WidgetsDelete = 'widgets.delete',
  WidgetsView = 'widgets.view',
  WidgetsViewAny = 'widgets.viewAny',

  CustomersView = 'customers.view',
  CustomersViewAny = 'customers.viewAny',
  // DEV-777 — narrow reception capability: recover/reshare a guest's access
  // code without the full CRM read granted by CustomersView*.
  CustomersViewAccessCode = 'customers.viewAccessCode',

  CategoriesCreate = 'categories.create',
  CategoriesEdit = 'categories.edit',
  CategoriesDelete = 'categories.delete',
  CategoriesView = 'categories.view',
  CategoriesViewAny = 'categories.viewAny',

  TagsCreate = 'tags.create',
  TagsEdit = 'tags.edit',
  TagsDelete = 'tags.delete',
  TagsView = 'tags.view',
  TagsViewAny = 'tags.viewAny',

  InternalLabelsCreate = 'internal_labels.create',
  InternalLabelsEdit = 'internal_labels.edit',
  InternalLabelsDelete = 'internal_labels.delete',
  InternalLabelsView = 'internal_labels.view',
  InternalLabelsViewAny = 'internal_labels.viewAny',

  RolesCreate = 'roles.create',
  RolesEdit = 'roles.edit',
  RolesDelete = 'roles.delete',
  RolesView = 'roles.view',
  RolesViewAny = 'roles.viewAny',

  PermissionsCreate = 'permissions.create',
  PermissionsEdit = 'permissions.edit',
  PermissionsDelete = 'permissions.delete',
  PermissionsView = 'permissions.view',
  PermissionsViewAny = 'permissions.viewAny',

  SettingsEditBase = 'settings.edit_base',
  SettingsEditLegal = 'settings.edit_legal',
  SettingsEditCustom = 'settings.edit_custom',

  TdlLeadsViewAny = 'tdl_leads.viewAny',

  SalesDemosCreate = 'sales_demos.create',
  SalesDemosEdit = 'sales_demos.edit',
  SalesDemosDelete = 'sales_demos.delete',
  SalesDemosView = 'sales_demos.view',
  SalesDemosViewAny = 'sales_demos.viewAny',

  ChainsViewAny = 'chains.viewAny',
  ChainsView = 'chains.view',
  ChainsCreate = 'chains.create',
  ChainsEdit = 'chains.edit',
  ChainsDelete = 'chains.delete',
  ChainsAssignLocations = 'chains.assignLocations',
  ChainsManageApiKeys = 'chains.manageApiKeys',

  ScheduleEditSlotCapacity = 'schedule.edit_slot_capacity',

  LocationPlacesViewAny = 'locationPlaces.viewAny',
  LocationPlacesView = 'locationPlaces.view',
  LocationPlacesCreate = 'locationPlaces.create',
  LocationPlacesEdit = 'locationPlaces.edit',
  LocationPlacesDelete = 'locationPlaces.delete',

  FattuttoManage = 'fattutto.manage',
}

export type BokunConfiguration = {
  id: number
  chainId: number
  username: string
  isActive: boolean
  hasPassword: boolean
  hasApiAccessKey: boolean
  hasApiSecretKey: boolean
}

export type MulesoftConfiguration = {
  chainId: number
  tokenUrl: string | null
  clientId: string | null
  hasClientSecret: boolean
  scope: string | null
  baseUrl: string | null
  timeout: number | null
  isConfigured: boolean
}

export type BokunProduct = {
  id: string
  name: string
  pricingCategories: { id: string; label: string }[]
}

export type Credentials = {
  email: string
  password: string
}

export type RoleData = {
  id: number
  name: string
  permissions: string[]
  created_at: string
  updated_at: string
}

export type User = {
  id: number
  email: string
  firstName: string
  lastName: string
  password?: string
  role: Role
  features: Record<string, boolean>
  company: Company
  companyId: number
  permissions: Permission[]
  manageableCompanies: Option[]
}

export type Category = {
  id: number
  name: string
  description: string
  icon: string
  slug: string | null
  fallbackLocale: string
  companyId: number
  experiencesCount?: number
  companiesCount?: number
  chainsCount?: number
  settings?: Record<string, boolean>
}

export type CategoryExperience = {
  id: number
  uuid: string
  name: string
  status: ExperienceStatus
  organizer: {
    id: number
    name: string
    chain: {
      id: number
      name: string
    } | null
  }
}

export type Tag = {
  id: number
  label: Record<string, string>
  fallbackLocale: string
  companyId: number
}

export type InternalLabel = {
  id: number
  label: Record<string, string>
  fallbackLocale: string
  companyId: number
}

export enum ExperienceStatus {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
}

export enum CancellationPolicy {
  // DEV-770 — no minimum advance: cancellable for free until the experience starts.
  H0 = '0h',
  H24 = '24h',
  H72 = '72h',
  D7 = '7d',
  NON_REFUNDABLE = 'non-refundable',
}

export enum ConfirmationType {
  INSTANT = 'instant',
  MANUAL = 'manual',
}

export type OpenHour = {
  id: number
  availableDays: string
  startsTime: string
  displayTime?: string
  endsTime: string
}

export type SeasonalPrice = {
  id: number | string
  retailRate: number
  startsAt: string
  endsAt: string
}

export type Price = {
  id: number | string
  name: string
  description: string
  unit: string
  unitName: string
  retailRate: number
  productId: string | number | null
  product: Product | null
  isExtra: boolean
  seasonalPrices: SeasonalPrice[]
  isFree: boolean
  hideFreeLabel: boolean
  nameSource?: SourceHint | null
  descriptionSource?: SourceHint | null
}

export type Attachment = {
  url: string
  type: 'image' | 'pdf'
  name?: string
}

export type ExperienceOption = {
  id?: number
  locale?: string
  name: string
  basePrice: number
  code: string
  confirmationType: ConfirmationType
  instantConfirmationThreshold?: number | null
  maxParticipants: number
  isPrivate: string
  days: number
  hours: number
  minutes: number
  dateRanges?: {
    id?: number
    from: string
    until: string
  }[]
  notes: string
  internalConfirmationMessage?: string
  externalConfirmationMessage?: string
  attachments: Attachment[]
  openHours: OpenHour[]
  prices: Price[]
  autoConfirmAtMinimum?: boolean
  minimumThreshold?: number
  minAge?: number | null
  maxAge?: number | null
  meetingPoint?: Address
  meetingNote?: string
  allowCustomPickingPoint?: boolean
  isCombo?: boolean
  bokunProductId?: string | null
  componentOptions?: {
    optionId: number
    label: string
    sortOrder: number
    option: ExperienceOption
  }[]
  nameSource?: SourceHint | null
  notesSource?: SourceHint | null
  meetingNoteSource?: SourceHint | null
  internalConfirmationMessageSource?: SourceHint | null
  externalConfirmationMessageSource?: SourceHint | null
}

export type ExperienceOptionException = {
  id?: number
  experienceOptionId: number
  startDate: string
  endDate?: string | null
  reason?: string | null
  createdAt?: string
  updatedAt?: string
}

export type CompanyTheme = {
  primaryColor: string
  textColor: string

  secondaryColor: string
  logo: string

  googleAnalyticsId?: string
  googleAnalyticsCrossDomainUrl?: string
}

export enum CompanyType {
  Seller = 'seller',
  Organizer = 'organizer',
  Chain = 'chain',
}

export type CompanyFattuttoDomain = {
  id: number
  domainId: string
  label: string | null
}

export const AZURE_AUTH_VERDICT = {
  OK: 'ok',
  REDIRECT_URI_MISMATCH: 'redirect_uri_mismatch',
  CLIENT_ID_UNKNOWN: 'client_id_unknown',
  UNREACHABLE: 'unreachable',
  PARSE_ERROR: 'parse_error',
} as const

export type AzureAuthVerdict =
  (typeof AZURE_AUTH_VERDICT)[keyof typeof AZURE_AUTH_VERDICT]

export const AZURE_AUTH_SEVERITY = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
} as const

export type AzureAuthSeverity =
  (typeof AZURE_AUTH_SEVERITY)[keyof typeof AZURE_AUTH_SEVERITY]

export type AzureAuthUrlComponents = {
  clientId: string
  redirectUri: string
  tenant: string
  policy: string
}

export type AzureAuthTestSummary = {
  at: string
  verdict: AzureAuthVerdict | null
  severity: AzureAuthSeverity | null
  message: string | null
}

export type AzureAuthTestResult = {
  verdict: AzureAuthVerdict
  severity: AzureAuthSeverity
  message: string | null
  errorCode: string | null
}

export type Company = {
  id: number
  uuid: string
  name: string
  logo: string
  email: string
  phone: string
  vat: string
  iban: string
  legalName: string
  address: Address
  website: string
  rate: number
  rateCount: number
  description?: string
  stripeAccount: string | undefined
  hasIndependentStripe: boolean
  allowsNonFiscalPayment?: boolean
  stripePublicKey?: string
  stripeSecretKey?: string
  sdi: string
  timezone: string
  type: string
  status: string
  theme: CompanyTheme
  fattuttoDomainId: string
  fattuttoDomains?: CompanyFattuttoDomain[]
  fallbackLocale: string
  authUrl?: string
  authUrlComponents?: AzureAuthUrlComponents | null
  lastAuthTest?: AzureAuthTestSummary | null
  enableExperienceAuth?: boolean
  enableMarketingConsent?: boolean
  privacyPolicyDocument?: string | null
  termsAndConditionsDocument?: string | null
  enableRecommendExperiences?: boolean
  chainId?: number | null
  chain?: {
    id: number
    name: string
    code: string
    fattuttoRequired?: boolean
    // DEV-777 — chain authenticates guests by codice univoco; gates the Clienti
    // access-code recovery surface.
    usesAccessCodes?: boolean
  } | null
  supportedLocales?: string[]

  // PWA settings for location carousel (pwaDescription is translatable)
  pwaDescription?: Record<string, string> | null
  pwaImage?: string | null
  pwaContactPhone?: string | null
  pwaContactEmail?: string | null

  // PWA Location Home settings
  pwaLogo?: string | null
  pwaWelcomeMessage?: Record<string, string> | null
  pwaHeroImage?: string | null
  pwaFooterImage?: string | null
  pwaEnabled?: boolean

  // Per-Location PWA identity (DEV-581) — slug + manifest overrides.
  // All four optional; when blank the Guest PWA inherits from the parent chain.
  slug?: string | null
  pwaName?: string | null
  pwaShortName?: string | null
  pwaIcon192?: string | null
  pwaIcon512?: string | null

  // DEV-850 — Opera hotel id used by the Mulesoft integration (locations only)
  pmsHotelId?: string | null

  // PWA Bottom Navigation configuration
  pwaBottomNavConfig?: BottomNavConfig | null
}

// ==================== Bottom Navigation Configuration ====================

export const BOTTOM_NAV_SLOT_KEYS = ['slot1', 'slot2', 'slot3', 'slot4'] as const;
export type BottomNavSlotKey = typeof BOTTOM_NAV_SLOT_KEYS[number];

export const NAV_LINK_TYPES = { Home: 'home', Profile: 'profile', Cart: 'cart' } as const;

export type BottomNavLinkType = 'home' | 'profile' | 'cart' | PageType;

export type BottomNavSlot = {
  icon: string | null
  label: Record<string, string> | null
  linkType: BottomNavLinkType | null
  linkUrl: string | null
  drawerId: number | null
}

export type BottomNavConfig = {
  slot1: BottomNavSlot | null
  slot2: BottomNavSlot | null
  slot3: BottomNavSlot | null
  slot4: BottomNavSlot | null
}

export type CustomQuestion = {
  id?: number
  experienceId?: number
  question: string // Will be handled as translatable by backend based on locale
  isRequired: boolean
  position: number
  questionSource?: SourceHint | null
}

export type CustomAnswer = {
  id?: number
  orderId?: number
  customQuestionId: number
  question?: string // Denormalized question text for display
  answer: string
  position?: number // Position of the original custom question
}

export type FocalPoint = {
  x: number
  y: number
}

export type ExperienceImage = {
  public_id: string
  coordinates?: { custom?: number[][] }
  focal_point?: FocalPoint
}

export type SourceHint<T = string> = {
  locale: string
  value: T
  isChainDefault: boolean
}

export type LocaleBucket = {
  filled: number
  total: number
}

export type LocaleStatus = LocaleBucket & {
  byStep?: Record<string, LocaleBucket>
  byOption?: Record<number, LocaleBucket>
}

export type EffectiveDefaultSource = 'chain' | 'experience' | 'config'

export type Experience = {
  id: number
  uuid: string
  name: string
  vatRateId: number
  category: Category
  categoryId?: number
  description: string
  organizerId: number
  status: ExperienceStatus
  images: ExperienceImage[] | string[]
  videos: string[]
  fallbackLocale: string
  effectiveDefaultLocale?: string
  effectiveDefaultSource?: EffectiveDefaultSource
  translationStatus?: Record<string, LocaleStatus>
  highlight: string
  inclusions: string[]
  exclusions: string[]
  notes: string
  location: Address
  meetingPoint: Address
  meetingNote: string
  pickingPoints: Address[]
  allowCustomPickingPoint: boolean
  cutoffMinutes: number
  postStartGraceMinutes: number
  resellerCommission: number
  cancellationPolicy: CancellationPolicy
  supportEmail: string
  supportPhone: string
  contactMethod: ContactMethod
  phone: string
  email: string
  options: ExperienceOption[]
  isInternal: boolean
  organizer: Company
  rate: number
  rateCount: number
  reservedCount: number
  isIncluded: boolean
  petAccess: boolean | null
  needValidation: boolean
  enableQuickBooking: boolean
  enableBuyAsVoucher?: boolean
  onlyBuyAsVoucher?: boolean
  enablePayOnSite?: boolean
  onlyPayOnSite?: boolean
  isArchived: boolean
  archivedAt: string | null
  accessibility: string
  tags: Tag[]
  internalLabels: InternalLabel[]
  staffs: User[]

  fattuttoDomainId: string | null
  fattuttoDocumentTypeId: string
  fattuttoCancelDocumentTypeId: string
  stripeCode: string
  stripeConnectedAccountId: string
  termsDocument: string | null
  privacyPolicyDocument: string | null

  authRequired: boolean
  distance?: number
  totalMinutes?: number
  customQuestions?: CustomQuestion[]

  isInformationPage: boolean
  isSimplifiedView: boolean
  trackGuestType?: boolean
  requireDateOfBirth?: boolean | null
  effectiveRequireDateOfBirth?: boolean
  allowsNonFiscalPayment?: boolean
  bokunEnabled?: boolean

  nameSource?: SourceHint | null
  descriptionSource?: SourceHint | null
  highlightSource?: SourceHint | null
  inclusionsSource?: SourceHint<string[]> | null
  exclusionsSource?: SourceHint<string[]> | null
  notesSource?: SourceHint | null
  accessibilitySource?: SourceHint | null
  meetingNoteSource?: SourceHint | null
}

export type Paginator<T> = {
  data: T[]
  meta: {
    total: number
    last_page: number
    current_page: number
  }
}

/**
 * A geographic address used by Experience.location, Experience.meeting_point,
 * Experience.picking_points[*] and ExperienceOption.meeting_point.
 *
 * Most string fields below come from the Google Places autocomplete result
 * (address components: city, region, country, postal code, etc.) and are not
 * always populated when the coordinates are entered manually or selected from
 * the place library — see `name` and `placeId` (DEV-84) for that flow.
 */
export type Address = {
  /**
   * Latitude/longitude are the only universally-present fields. The string
   * descriptors below come from Google Places address components and are
   * intentionally optional: they may be empty when the coordinates were
   * entered manually or selected from the place library (DEV-84).
   */
  latitude: number
  longitude: number
  country?: string
  countryCode?: string
  address?: string
  city?: string
  postalCode?: string
  region?: string
  /** Structured, geocoder-formatted single-line address (Google Places). */
  formattedAddress?: string
  /** Legacy snake_case alias retained for backwards compatibility with older payloads. */
  formatted_address?: string
  /**
   * DEV-84 / DEV-663 — translations map of the place name from the LocationPlace
   * library (e.g. `{it: "Centro sportivo", en: "Sports centre"}`). Used as a
   * display fallback when there is no structured `formattedAddress` (typical
   * for manually entered coordinates). Resolved via `getLocalizedText` against
   * the current locale.
   */
  name?: Record<string, string>
  /**
   * DEV-84 — id of the LocationPlace library entry this snapshot was taken
   * from. Tracking only: editing the library place does NOT propagate back
   * to existing experiences (snapshot semantics).
   */
  placeId?: number
}

/**
 * DEV-84 — origin of a LocationPlace entry. `google` means coordinates and
 * formatted address came from the Google Places autocomplete; `manual` means
 * the user typed lat/lng directly and the address line is intentionally
 * absent.
 */
export const PLACE_SOURCE = {
  GOOGLE: 'google',
  MANUAL: 'manual',
} as const

export type PlaceSource = (typeof PLACE_SOURCE)[keyof typeof PLACE_SOURCE]

/**
 * DEV-84 — a reusable named coordinate owned by a single Company (location).
 * Experiences and options reference one of these by id and persist a
 * coordinate snapshot inline; editing the place here does not retroactively
 * update already-configured experiences.
 */
export type LocationPlace = {
  id: number
  companyId: number
  /** DEV-663 — translations map keyed by locale (e.g. `{it: "Centro sportivo", en: "Sports centre"}`). */
  name: Record<string, string>
  /**
   * DEV-666 — the owning company's `fallback_locale`. The admin UI localises
   * `name` client-side via `getLocalizedText`, which needs the per-company
   * fallback to honour the company default when the active UI locale has no
   * entry for this place.
   */
  fallbackLocale: string | null
  latitude: number
  longitude: number
  formattedAddress: string | null
  source: PlaceSource
}

export type CreateLocationPlaceDTO = {
  /** DEV-663 — translations map (e.g. `{it: "...", en: "..."}`). At least one entry required. */
  name: Record<string, string>
  latitude: number
  longitude: number
  formatted_address?: string | null
  source: PlaceSource
}

export type UpdateLocationPlaceDTO = Partial<CreateLocationPlaceDTO>

export const FALLBACK_COVER_ID = 'mrunjkqldk6ojyfxjvj1'

export type Contact = {
  firstName: string
  lastName: string
  phone: string
  email: string
  needInvoice?: boolean
  invoiceType?: string
  billingAddress?: Address
  legalName?: string
  taxCode?: string
  sdi?: string
  pec?: string
  dateOfBirth?: string
  accountId?: string
  termsAccepted?: boolean
  privacyPolicyAccepted?: boolean
  marketingConsent?: boolean
  recommendExperiences?: boolean
  specialCategoriesConsent?: boolean
  customAnswers?: { [orderId: string]: { [questionId: string]: string } }
  // DEV-728 — id of the existing customer the operator associated via search.
  customerId?: number | null
}

export enum MasterOrderStatus {
  Cart = 'cart',
  Unpaid = 'unpaid',
  WaitingConfirmation = 'waiting_confirmation',
  Placed = 'placed',
  Completed = 'completed',
  Canceled = 'canceled',
}

export enum OrderStatus {
  Cart = 'cart',
  Unpaid = 'unpaid',
  WaitingConfirmation = 'waiting_confirmation',
  Ongoing = 'ongoing',
  Scheduled = 'scheduled',
  Completed = 'completed',
  Canceled = 'canceled',
  Voucher = 'voucher',
  Delay = 'delay',
}

export enum PaymentMethod {
  POS = 'pos',
  Card = 'card',
  PaymentLink = 'payment_link',
  Cash = 'cash',
  Other = 'other',
  OnSite = 'on_site',
  Free = 'free',
}

export const GUEST_TYPE = {
  Internal: 'internal',
  External: 'external',
  NotTracked: 'not_tracked',
} as const

export const ATTRIBUTION_SOURCE = {
  SelfDeclared: 'self_declared',
  OperatorManual: 'operator_manual',
} as const

export type MasterOrder = {
  id: string
  _id: string
  contacts: Contact[]
  status: MasterOrderStatus
  source: string
  paymentIntentId: string
  amount: number
  locale: string
  paymentMethod: string
  orders: Order[]
  customer: Contact
  seller: Company
  widget?: Widget
  additionalNotes: string
  createdAt: string
  finalAmount?: number
}

export type Reservation = {
  id: number
  inventoryId: number | null
  inventory: Inventory | null
  startAt: string
  endAt: string
  completedAt: string | null
}

export type OrderItem = {
  id: number
  name: string
  amount: number
  quantity: number
  unitPrice: number
  reservations: Reservation[]
  product?: Product
  priceId?: number
  componentOptionId?: number
  componentOptionName?: string
  displayTime?: string
  // DEV-810: combo legs validate independently; this is the item's own
  // check-in stamp (null until its leg is validated).
  validatedAt?: string | null
  // DEV-429 #32: who the seat is for, on guest RBA group bookings. Null for
  // standard bookings or when the participant is not attributed.
  participantName?: string | null
}

export type Earning = {
  type: 'sale' | 'refund'
  transactionDate: string
  amountNet: number
  id: number
  amount: number
  comission: number
  experience: Experience
  items: OrderItem[]
  createdAt: string
  startsAt: string
  displayTime?: string
  endsAt: string
  seller: Company
  source: string
  customer: Contact
  status: OrderStatus
  masterOrder: MasterOrder
  paymentMethod: string
  // DEV-797: operator credited with the sale — payer for "Pay on site", else creator.
  operator: User
  isCanceled: boolean
  canceledAt?: string
  canceledBy?: User
  refundId?: string
  // DEV-370: classifica la rivendita (Group / External) per il seller
  resellType?: 'own' | 'group' | 'external' | null
  isResold?: boolean
}

export type Order = {
  id: number
  amount: number
  name: string
  image: string
  quantity: number
  experience: Experience
  experienceName: string
  option: ExperienceOption
  optionName: string
  items: OrderItem[]
  date: string
  startsAt: string
  endsAt: string
  pickupPoint: Address
  createdAt: string
  createdAtISO: string
  startsAtISO: string
  endsAtISO: string
  displayTime?: string
  customer: Contact
  status: OrderStatus
  paymentMethod: string
  seller: Company
  masterOrderId: number
  source: string
  cancellableBefore: string
  masterOrder: MasterOrder
  transferId: string
  needValidation: boolean
  validatedAt: string | null
  /** Roll-call status captured via the staff PWA: 'present' | 'absent' | null. */
  attendance: 'present' | 'absent' | null
  // DEV-668: confirmed_at is set when the booking is confirmed; for OnSite
  // orders it can be non-null while status is still Unpaid (confirmed but
  // awaiting payment). placed_at marks the payment moment.
  confirmedAt: string | null
  placedAt: string | null
  isBlocked: boolean
  isQuickBooking: boolean

  fattuttoId: string
  fattuttoUrl?: string
  cancelFattuttoId: string
  cancellationFattuttoUrl?: string
  createdBy: User
  customAnswers?: CustomAnswer[]
  createdVoucher?: Voucher
  usedVoucher?: Voucher
  baseAmount?: number
  reductionAmount?: number
  guestType?: 'internal' | 'external' | 'not_tracked'
  // DEV-370
  resellType?: 'own' | 'group' | 'external' | null
  attributionSource?: 'self_declared' | 'operator_manual' | 'crm_verified'
  rescheduledAt?: string
  previousStartsAt?: string
  previousEndsAt?: string
  previousDisplayTime?: string
  isVoucher?: boolean
}

export type Voucher = {
  id: number
  code: string
  status: VoucherStatus
  // Mirrors backend VoucherResource (post Plan 01 cleanup): source + voucherGroupId
  // are now native fields on the resource — no parallel slim payload on Order.
  source?: VoucherSource | null
  voucherGroupId?: string | null
  externalReference?: string | null
  expiresAt: string
  redeemedAt: string
  createdAt: string
  updatedAt: string

  createdByOrder?: Order
  usedByOrder?: Order
}

export type CreateOrderRequest = {
  selectedDate: Date | undefined
  selectedOption: string | undefined
  startsSlot: string | undefined
  selectedPrices: Record<number | string, number>
  selectedPoint: Address | undefined
  source: string
  sellerId: number
  creatorId?: number
  isQuickBooking?: boolean
  isBuyingVoucher?: boolean
  originalOrderId?: number
  widgetUuid?: string
  // DEV-730: operator "Book Now" flow opts in to bypass the experience cutoff.
  book_now?: boolean
  componentSelections?: {
    componentOptionId: number
    date: string
    slot: string
  }[]
}

export type CalendarEvent = {
  id: string
  title: string
  start: string
  end: string
  allDay?: boolean
  /** FullCalendar: `'block'` avoids month-view list-item (dot) rendering */
  display?: 'auto' | 'block' | 'list-item' | 'background' | 'inverse-background' | 'none'
  experienceId: number
  count: number
  color: string
  orderIds: number[]
  optionId?: number
  /** FullCalendar: schedule rows use unified option styling */
  backgroundColor?: string
  borderColor?: string
  textColor?: string
  /** FullCalendar / API: e.g. `participantsCount`, `orderIds`; schedule uses `scheduleTooltipBody`. */
  extendedProps?: Record<string, unknown> & {
    scheduleTooltipBody?: string
  }
}

/** One bookable time slot in schedule API (date + open-hour slot). */
export type ScheduleSlotRow = {
  date: string
  slot: string
  displayTime: string
  capacity: {
    /** `null` when the option uses individual inventory (no per-slot cap). */
    total: number | null
    available: number | null
  }
  status: 'available' | 'limited' | 'full'
  /** Id of the per-slot capacity override, when one is set for this (date, slot). */
  slotOverrideId: number | null
  /**
   * Option default `max_participants`, regardless of any override.
   * `null` when the option uses individual inventory and therefore has no per-slot cap.
   */
  defaultMaxParticipants: number | null
  /**
   * `true` when the option uses individual inventory (per-product SKU units)
   * instead of a numeric per-slot cap. Frontend should render an unlimited
   * indicator instead of a 0/0 ratio for these slots.
   */
  usesIndividualInventory: boolean
}

export type ExperienceOptionSlotOverride = {
  id: number
  experienceOptionId: number
  date: string
  slotTime: string
  maxParticipants: number
  reason?: string | null
  createdByUserId?: number | null
  createdAt?: string
  updatedAt?: string
}

export type ExperienceOptionSlotOverridePayload = {
  date: string
  slotTime: string
  maxParticipants: number
  reason?: string | null
}

/**
 * One (option × date) aggregate row emitted by the schedule summary endpoint
 * (DEV-687). Powers the month view: each cell shows the day's slot counters
 * and, for individual-inventory options, the available/total inventory stock.
 *
 * Per-slot detail is fetched on demand via `getScheduleDetail` when the user
 * clicks a cell.
 */
export type ScheduleSummaryRow = {
  experienceId: number
  experienceUuid: string
  experienceName: string
  optionId: number
  optionName: string
  /** YYYY-MM-DD */
  date: string
  slotsTotal: number
  slotsAvailable: number
  slotsLimited: number
  slotsFull: number
  usesIndividualInventory: boolean
  /** `null` for slot-based options (no inventory layer). */
  inventoryTotal: number | null
  inventoryAvailable: number | null
}

export type InsightRecord = {
  date: string
  count: number
  amount: number
}

export type Inventory = {
  id: number
  sku: string
  status: string
  timeUsed: number
  hoursUsed: number
  product: Product
  inUse: boolean
  company: Company
}

export enum InventoryStatus {
  OutOfUse = 'out_of_use',
  InUse = 'in_use',
}

export type OccupiedReservation = {
  reservationId: number
  inventory: { id: number; sku: string } | null
  returnsAt: string
  order: { id: number; uid: string } | null
}

export type ProductOccupancy = {
  total: number
  occupied: number
  reservations: OccupiedReservation[]
}

export type Product = {
  id: number
  sku: string
  name: string
  photos: string[]
  inventories: Inventory[]
  inventoryCount: number
  company: Company
  fallbackLocale: string
}

export enum Status {
  Active = 'active',
  Inactive = 'inactive',
}

export type Store = {
  uid: string
  name: string
  photo: string
  status: Status
  createdAt: string
  company: Company
}

export type Widget = {
  id: number
  uuid: string
  storeId: string
  name: string
  status: Status
  company: Company
  createdAt: string
  store: Store
  fallbackLocale: string
  supportedLocales: string[]
}

export type Option = {
  value: string | number
  label: string
}

export type TranslatableOption = {
  value: string | number
  label: Record<string, string> | string
}

export type CategoryOption = Option & { description: string; icon: string }

export type TdlLead = {
  id: number
  data: string
  createdAt: string
}

export enum SalesDemoStatus {
  Active = 'active',
  Inactive = 'inactive',
  Draft = 'draft',
}

export enum SalesDemoPlacementMethod {
  Replace = 'replace',
  Append = 'append',
  Prepend = 'prepend',
  After = 'after',
  Before = 'before',
}

export type SalesDemo = {
  uuid: string
  hotelName: string
  slug: string
  title: string
  htmlContent: string
  widgetId?: number
  widgetPlacementSelector?: string
  widgetPlacementMethod?: SalesDemoPlacementMethod
  status: SalesDemoStatus
  previewImage?: string
  notes?: string
  createdBy: number
  createdAt: string
  updatedAt: string
  creator: User
  widget?: Widget & {
    store: Store & {
      company: Company
    }
  }
}

export type ChainTheme = {
  primaryColor: string
  secondaryColor?: string
  textColor?: string // Text color for buttons (contrast with primaryColor)
  linkColor?: string
  fontFamily?: string
  // Button styles
  buttonBorderRadius?: ButtonBorderRadius
  buttonShadow?: ButtonShadow
  buttonBorderWidth?: ButtonBorderWidth
  buttonBorderColor?: string
  buttonFontWeight?: ButtonFontWeight
  buttonTextTransform?: ButtonTextTransform
}

export type RegistrationField = {
  key: string
  required: boolean
}

export type Chain = {
  id: number
  name: string
  code: string
  logo: string | null
  email: string | null
  locationsCount: number
  locations?: Company[]
  createdAt: string
  updatedAt: string
  // Guest PWA fields
  guestPwaEnabled: boolean
  /**
   * Optional per-Chain custom domain (e.g. "biboapp.it") used as the apex
   * of a per-Location PWA. Unique across all chains. Subdomains of this
   * host (`{slug}.biboapp.it`) map to Companies via `companies.slug`.
   */
  customDomain: string | null
  pwaName: string | null
  pwaShortName: string | null
  pwaIcon192: string | null
  pwaIcon512: string | null
  theme: ChainTheme | null
  welcomeText: Record<string, string> | null
  heroImage: string | null
  defaultLocale: string
  supportedLocales: string[]
  // Chain info fields
  description: Record<string, string> | null
  contactPhone: string | null
  contactAddress: string | null
  supportEmail: string | null
  // Guest auth fields
  guestAuthProvider: string
  guestAuthMethods: string[]
  requireAuthForCart: boolean
  requireGuestRegistration: boolean
  // DEV-728 — staff PWA: allow creating new customers during booking
  staffCanCreateCustomer: boolean
  registrationFormFields: RegistrationField[] | null
  privacyPolicyUrl: string | null
  dataProcessingUrl: string | null
  termsUrl: string | null
  enableExperienceAuth?: boolean
  contactMethod: ContactMethod
  // Billing / compliance
  fattuttoRequired?: boolean
}

export type UpdateChainBillingDTO = {
  fattuttoRequired: boolean
}

export type UpdateGuestPwaDTO = {
  guestPwaEnabled?: boolean
  customDomain?: string | null
  pwaName?: string | null
  pwaShortName?: string | null
  pwaIcon192?: string | null
  pwaIcon512?: string | null
  theme?: ChainTheme | null
  welcomeText?: Record<string, string> | null
  heroImage?: string | null
  supportedLocales?: string[]
  // Chain info fields
  description?: Record<string, string> | null
  contactPhone?: string | null
  contactAddress?: string | null
  supportEmail?: string | null
  // Guest auth fields
  guestAuthMethods?: string[]
  requireAuthForCart?: boolean
  // DEV-728 — staff PWA: allow creating new customers during booking
  staffCanCreateCustomer?: boolean
  registrationFormFields?: RegistrationField[] | null
  privacyPolicyUrl?: string | null
  dataProcessingUrl?: string | null
  termsUrl?: string | null
  enableExperienceAuth?: boolean
  contactMethod?: ContactMethod
}

export type ChainDeveloper = {
  id: number
  chain_id: number
  email: string
  is_active: boolean
  must_reset_password: boolean
  created_at: string
  updated_at: string
  temporary_password?: string // Only present in creation response
}

export type CreateChainDeveloperDTO = {
  email: string
}

export enum ApiScope {
  ExperiencesRead = 'experiences:read',
  OrdersRead = 'orders:read',
  OrdersCreate = 'orders:create',
  VouchersCreate = 'vouchers:create',
  GuestStaysWrite = 'guest_stays:write',
}

export type ExternalApiKey = {
  id: number
  chain_id: number
  name: string
  scopes: string[]
  is_active: boolean
  rate_limit: number | null
  last_used_at: string | null
  created_at: string
  updated_at: string
  plain_key?: string // Only present in creation response
  chain?: Chain
}

export type ExternalApiLog = {
  id: number
  external_api_key_id: number
  endpoint: string
  method: string
  status_code: number
  query_params: Record<string, any> | null
  ip_address: string
  user_agent: string | null
  response_time_ms: number
  error_message: string | null
  created_at: string
  api_key?: ExternalApiKey
}

export type CreateExternalApiKeyDTO = {
  name: string
  scopes: string[]
  rate_limit?: number | null
}

export type UpdateExternalApiKeyDTO = {
  name?: string
  scopes?: string[]
  rate_limit?: number | null
  is_active?: boolean
}

// ==================== Location Home Configuration ====================

export enum SectionStyle {
  Normal = 'normal',
  Arch = 'arch',
}

export enum PageType {
  Activities = 'activities',
  Services = 'services',
  Map = 'map',
  Photos = 'photos',
  PracticalInfo = 'practical_info',
  OpeningHours = 'opening_hours',
  MyWayToPark = 'my_way_to_park',
  Restaurants = 'restaurants',
  ExperienceCatalog = 'experience_catalog',
  ExternalUrl = 'external_url',
  Custom = 'custom',
  // DEV-607 — single experience link; link_url stores the experience UUID.
  Experience = 'experience',
}

/** Frontend-only link type for custom pages (not a backend PageType) */
export const CUSTOM_PAGE_LINK_TYPE = 'custom_page'

export type LocationSection = {
  id: number
  companyId: number
  title: Record<string, string>
  position: number
  style: SectionStyle
  cardsPerRow: number
  isActive: boolean
  cards?: LocationSectionCard[]
  createdAt: string
  updatedAt: string
}

export type LocationSectionCard = {
  id: number
  locationSectionId: number
  title: Record<string, string>
  description: Record<string, string> | null
  image: string | null
  linkType: PageType | null
  linkUrl: string | null
  drawerId: number | null
  styleConfig: {
    overlayColor?: string
    overlayOpacity?: number
    textColor?: string
  } | null
  position: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export const BANNER_DISPLAY_MODES = ['classic', 'editorial'] as const
export type BannerDisplayMode = (typeof BANNER_DISPLAY_MODES)[number]

export type LocationBanner = {
  id: number
  companyId: number
  image: string | null
  displayMode: BannerDisplayMode
  buttonText: Record<string, string>
  drawerContent: Record<string, string> | null
  drawerBackgroundColor: string | null
  drawerBackgroundImage: string | null
  externalUrl: string | null
  // DEV-607 — unified opener fields (mirror LocationSectionCard).
  linkType: PageType | string | null
  linkUrl: string | null
  drawerId: number | null
  position: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type CreateLocationSectionDTO = {
  title: Record<string, string>
  style: SectionStyle
  cards_per_row: number
  is_active?: boolean
}

export type UpdateLocationSectionDTO = {
  title?: Record<string, string>
  style?: SectionStyle
  cards_per_row?: number
  is_active?: boolean
}

export type CreateLocationSectionCardDTO = {
  title: Record<string, string>
  description?: Record<string, string> | null
  image?: string | null
  link_type?: PageType | null
  link_url?: string | null
  drawer_id?: number | null
  style_config?: {
    overlayColor?: string
    overlayOpacity?: number
    textColor?: string
  } | null
  is_active?: boolean
}

export type UpdateLocationSectionCardDTO = {
  title?: Record<string, string>
  description?: Record<string, string> | null
  image?: string | null
  link_type?: PageType | null
  link_url?: string | null
  drawer_id?: number | null
  style_config?: {
    overlayColor?: string
    overlayOpacity?: number
    textColor?: string
  } | null
  is_active?: boolean
}

export type CreateLocationBannerDTO = {
  image?: string | null
  display_mode?: BannerDisplayMode
  button_text: Record<string, string>
  drawer_content?: Record<string, string> | null
  external_url?: string | null
  link_type?: PageType | string | null
  link_url?: string | null
  drawer_id?: number | null
  position?: number
  is_active?: boolean
}

export type UpdateLocationBannerDTO = {
  image?: string | null
  display_mode?: BannerDisplayMode
  button_text?: Record<string, string>
  drawer_content?: Record<string, string> | null
  external_url?: string | null
  link_type?: PageType | string | null
  link_url?: string | null
  drawer_id?: number | null
  position?: number
  is_active?: boolean
}

// =============================================================================
// LOCATION DRAWER TYPES
// =============================================================================

export enum DrawerType {
  PracticalInfo = 'practical_info',
  OpeningHours = 'opening_hours',
  MyWayToPark = 'my_way_to_park',
  Photos = 'photos',
  Custom = 'custom',
}

export const CUSTOM_DRAWER_LINK_TYPE = 'custom_drawer'

export type LocationDrawer = {
  id: number
  companyId: number
  type: DrawerType
  slug: string | null
  title: Record<string, string> | null
  headerImage: string | null
  headerImageUrl: string | null
  contentConfig: Record<string, unknown>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type DrawerTypeInfo = {
  type: DrawerType
  drawerId: number | null
  exists: boolean
}

export type CreateLocationDrawerDTO = {
  type: DrawerType
  slug?: string | null
  title?: Record<string, string> | null
  header_image?: string | null
  content_config?: Record<string, unknown>
  is_active?: boolean
}

export type UpdateLocationDrawerDTO = {
  slug?: string | null
  title?: Record<string, string> | null
  header_image?: string | null
  content_config?: Record<string, unknown>
  is_active?: boolean
}

// ========== Location PWA Experiences Collection ==========

export type LocationPwaExperience = {
  id: number
  companyId: number
  experienceId: number
  position: number
  isActive: boolean
  experience?: Experience
  createdAt: string
  updatedAt: string
}

// ========== Location Pages (Activities, Services, etc.) ==========

export type ActivitiesPageConfig = {
  timeSections: {
    morningStartsAt: string
    afternoonStartsAt: string
    eveningStartsAt: string
  }
  showAllDaySection: boolean
  calendarDays: number
  filters: {
    showPriceFilter: boolean
    showAgeFilter: boolean
    showDurationFilter: boolean
    showTimeFilter: boolean
    showCategoryFilter: boolean
    showTagFilter: boolean
    categoryIds: number[]
    tagIds: number[]
  }
}

export type ExperienceCatalogPageConfig = {
  defaultVisibleCount: number
}

export type MapPinConfig = {
  icon?: string | null
  latitude?: number | null
  longitude?: number | null
}

export type MapPageConfig = {
  mapCenter?: { lat: number; lng: number } | null
  mapZoom?: number | null
}

export type LocationPage = {
  id: number
  companyId: number
  type: PageType
  slug: string | null
  title: Record<string, string>
  description: Record<string, string>
  coverImage: string | null
  config: Record<string, any> | null
  position: number
  isActive: boolean
  experiences?: Experience[]
  createdAt: string
  updatedAt: string
}

export type CreateLocationPageDTO = {
  type: PageType
  slug?: string | null
  title: Record<string, string>
  description?: Record<string, string>
  cover_image?: string | null
  config?: Record<string, any> | null
  is_active?: boolean
}

export type UpdateLocationPageDTO = {
  slug?: string | null
  title?: Record<string, string>
  description?: Record<string, string>
  cover_image?: string | null
  config?: Record<string, any> | null
  is_active?: boolean
}

export type FilterOption = {
  id: number
  name?: string
  label?: string
}

export type FilterOptionsResponse = {
  categories: FilterOption[]
  tags: FilterOption[]
}

// ==================== Location Page Sections & Cards ====================

export type ExperienceDisplayConfig = {
  subtitle?: Record<string, string> | null
  badgeText?: Record<string, string> | null
  ctaBadgeText?: Record<string, string> | null
  timeDisplay?: string | null
}

export type SectionExperience = {
  id: number
  uuid: string
  name: string
  category: { id: number; name: string } | null
  position: number
  displayConfig?: ExperienceDisplayConfig | null
}

export type CardType = 'hero' | 'compact'

export type LocationPageSection = {
  id: number
  locationPageId: number
  title: Record<string, string>
  icon?: string | null
  cardType?: CardType
  position: number
  isActive: boolean
  cards?: LocationPageSectionCard[]
  experiences?: SectionExperience[]
  createdAt: string
  updatedAt: string
}

export type LocationPageSectionCard = {
  id: number
  locationPageSectionId: number
  title: Record<string, string>
  description: Record<string, string> | null
  icon: string | null
  ctaText: Record<string, string> | null
  linkType: PageType | null
  linkUrl: string | null
  drawerId: number | null
  position: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type CreateLocationPageSectionDTO = {
  title: Record<string, string>
  icon?: string | null
  card_type?: CardType
}

export type UpdateLocationPageSectionDTO = {
  title?: Record<string, string>
  icon?: string | null
  card_type?: CardType
  is_active?: boolean
}

export type CreateLocationPageSectionCardDTO = {
  title: Record<string, string>
  description?: Record<string, string> | null
  icon?: string | null
  cta_text?: Record<string, string> | null
  link_type?: PageType | null
  link_url?: string | null
  is_active?: boolean
}

export type UpdateLocationPageSectionCardDTO = CreateLocationPageSectionCardDTO
